/**
 * The team API (agents first, train 1b): the roster, creating an agent by
 * hand, and an agent's own exchange log. Structured answers throughout: a
 * refusal is a code the sheet can name, never a message it has to parse.
 */
import type {
  ComposerHistoryWireEntry,
  CreateAgentInput,
  TeamAgent,
} from "@bash/shared";
import { apiUrl } from "./base";
import { authedFetch, getOptionalAccessToken } from "./client";

export async function listTeam(signal?: AbortSignal): Promise<TeamAgent[]> {
  // Throwing on a missing token turns the cold-launch token race into a fast
  // rail retry instead of an unauthenticated 401 (the routines read's law).
  const token = await getOptionalAccessToken();
  if (!token) throw new Error("no_token");
  const res = await fetch(apiUrl("/api/team"), {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });
  // A 404 is the documented dark state: the server flag is off and every team
  // route answers it. Throwing would put the converging rail on a retry every
  // ten seconds, for as long as the two flags disagree, each one reporting
  // itself. An empty roster is the answer, and the caller keeps the strip
  // hidden. ONLY 404: any other non-OK is a failure and must never be read as
  // "this person has no team", which would clobber a roster that is there.
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`team_${res.status}`);
  const body = (await res.json()) as { agents?: TeamAgent[] };
  // A 200 always carries the array (the lead is minted on read), so a body
  // without one is a broken answer, not an empty team: it throws and the rail
  // retries rather than painting a team that was never read.
  if (!Array.isArray(body.agents)) throw new Error("team_malformed");
  return body.agents;
}

export type CreateTeamAgentResult =
  | { ok: true; agent: TeamAgent }
  | {
      ok: false;
      code:
        | "handle_taken"
        | "handle_invalid"
        | "handle_reserved"
        | "invalid_input"
        | "unavailable";
      message: string;
    };

type RefusalCode = Exclude<CreateTeamAgentResult, { ok: true }>["code"];

/** Typed to the code union rather than to string: a member that the union does
 *  not name (a typo, a code the routes stopped sending) is a compile error
 *  here instead of a refusal that reaches the sheet as `unavailable`. */
const REFUSALS: ReadonlySet<RefusalCode> = new Set<RefusalCode>([
  "handle_taken",
  "handle_invalid",
  "handle_reserved",
  "invalid_input",
]);

export async function createTeamAgent(
  input: CreateAgentInput
): Promise<CreateTeamAgentResult> {
  try {
    const token = await getOptionalAccessToken();
    if (!token)
      return {
        ok: false,
        code: "unavailable",
        message: "Sign in again to add an agent.",
      };
    const res = await fetch(apiUrl("/api/team"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    });
    const body = (await res.json().catch(() => null)) as {
      agent?: TeamAgent;
      error?: string;
      code?: string;
      message?: string;
    } | null;
    if (res.ok && body?.agent) return { ok: true, agent: body.agent };
    // The team routes answer { error: <code>, message } (1a Task 5); `code` is
    // read only as a fallback.
    const raw = body?.error ?? body?.code;
    const code: RefusalCode =
      raw && (REFUSALS as ReadonlySet<string>).has(raw)
        ? (raw as RefusalCode)
        : "unavailable";
    return {
      ok: false,
      code,
      message: body?.message ?? "Could not add the agent right now.",
    };
  } catch {
    return {
      ok: false,
      code: "unavailable",
      message: "Could not reach Vestra. Try again.",
    };
  }
}

/** The agent's exchange log, newest first; [] when signed out or on any failure. */
export async function fetchAgentHistory(
  handle: string
): Promise<ComposerHistoryWireEntry[]> {
  try {
    const res = await authedFetch(
      `/api/team/${encodeURIComponent(handle)}/history`
    );
    const body = (await res.json()) as { entries?: ComposerHistoryWireEntry[] };
    return Array.isArray(body.entries) ? body.entries : [];
  } catch {
    return [];
  }
}
